import React, { useEffect, useState } from "react";
import {
  AppBar,
  Box,
  Button,
  Divider,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import styled from "@emotion/styled";
import {
  CarePlanIcon,
  EditCarePlanIcon,
  VersionApproveTitleIcon,
} from "../../../assets/constants";
import HighlightOffRoundedIcon from "@mui/icons-material/HighlightOffRounded";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { dateFormat } from "../../../utils/helpers";
import CarePlanProgress from "../CarePlanProgress";
import AlertDialog from "../../AlertDialog";
import VersionApproveContent from "../VersionApproveContent";
import {
  editCarePlanFunc,
  getCarePlanFunc,
  resetSavePlanVersion,
  savePlanVersionFunc,
} from "../../../actions/CarePlan";
import { useDispatch } from "react-redux";
import RectangleSkeletons from "../../RectangleSkeletons";

const StyledTypography = styled(Typography)(() => ({
  fontWeight: 500,
  color: "#212121",
}));

const StyledAppBar = styled(AppBar)(({ theme, padding }) => ({
  background: "#F9F9FA !important",
  position: "fixed",
  height: "97px",
  padding: "24px 24px 8px 24px",
  top: "0",
  left: "auto",
  display: "flex",
  justifyContent: "space-between",
  zIndex: "100",
  [theme.breakpoints.down("md")]: {
    width: "100%",
    left: "0",
  },
}));

const CarePlanNavbar = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [openModal, setOpenModal] = useState(false);
  const [progressValue, setProgressValue] = useState(0);

  const getCarePlan = useSelector((state) => state.getCarePlan);
  const { loading: loadingCarePlan, carePlan } = getCarePlan;

  const savePlanVersion = useSelector((state) => state.savePlanVersion);
  const { loading: loadingSave, savedPlanVersion } = savePlanVersion;

  const getPlanVersionsList = useSelector((state) => state.getPlanVersionsList);
  const { loading: loadingPlanVersions, carePlanVersionsList } =
    getPlanVersionsList;

  const editCarePlan = useSelector((state) => state.editCarePlan);
  const { isEditCarePlan } = editCarePlan;

  const carePlanDetail = carePlan?.care_plans[0];

  const draftVersion =
    carePlanVersionsList?.versions?.find((item) => item?.status === "draft") ||
    null;

  const showEditButton =
    !loadingPlanVersions &&
    carePlanDetail?.progress_percentage === 100 &&
    !draftVersion &&
    !isEditCarePlan;
  const showUpdateCarePlanButton = draftVersion ? true : false;

  const isEditingCarePlan =
    carePlanDetail?.progress_percentage === 100 &&
    (draftVersion || isEditCarePlan)
      ? true
      : false;

  const { id: patientId } = useParams();

  const openModalHandler = () => {
    setOpenModal(true);
  };

  useEffect(() => {
    // if (!carePlan?.care_plans?.length) return;

    const newProgress = carePlan?.care_plans[0]?.progress_percentage ?? 0;

    setProgressValue((prev) => {
      if (newProgress === 0 && prev !== 0) return prev;

      return newProgress;
    });
  }, [carePlan]);

  const saveVersionHandler = () => {
    const versionObj = {
      care_plan_id: carePlanDetail?.id,
      version_id: draftVersion?.id,
    };

    dispatch(savePlanVersionFunc(versionObj));
  };

  useEffect(() => {
    if (savedPlanVersion?.ok) {
      dispatch(editCarePlanFunc(false));
      dispatch(resetSavePlanVersion());
      // const carePlanObj = { patient_id: patientId };
      dispatch(getCarePlanFunc(patientId));
      setOpenModal(false);
      navigate(`/patient/${patientId}?tab=overview`);
    }
  }, [savedPlanVersion]);

  const editCarePlanHandler = () => {
    dispatch(editCarePlanFunc(true));
  };

  return (
    <>
      {openModal && (
        <AlertDialog
          openModal={openModal}
          isLoading={loadingSave}
          maxWidth="653px"
          closeModal={() => setOpenModal(false)}
          closeOverlay={false}
          data={{
            title: (
              <>
                <VersionApproveTitleIcon />
                {isEditingCarePlan ? "Save Care Plan" : "Care Plan Complete"}
              </>
            ),
          }}
          submitText={isEditingCarePlan ? "Update Care Plan" : "Save Care Plan"}
          cancelColor="primary"
          textTransform="capitalize"
          padding="24px"
          actionsPaddingTop="0"
          cancelText="Back to Editing"
          footerTitle={true}
          footerText={
            isEditingCarePlan
              ? `By saving, you confirm that this updated care plan has been reviewed, instructed, or built under the direction of the supervising physician. This version will be saved as Version ${draftVersion?.version_number} and older versions can be found in Versions History.`
              : " By saving, you confirm that this care plan has been reviewed, instructed, or built under the direction of the supervising physician."
          }
          formSubmitFunction={saveVersionHandler}
        >
          <VersionApproveContent />
        </AlertDialog>
      )}

      <StyledAppBar elevation={0}>
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
        >
          <Stack gap="8px">
            <Stack direction="row" alignItems="center" gap="8px">
              <CarePlanIcon />
              <Typography variant="h5" color="#212121" fontSize="1.8rem">
                Care Plan Workspace
              </Typography>
            </Stack>
            {loadingCarePlan ? (
              <RectangleSkeletons totalRows={1} height={20} width="400px" />
            ) : (
              <Stack direction="row" alignItems="center" gap="16px">
                <Typography variant="body2" color="#212121">
                  {carePlanDetail?.patient?.name}
                </Typography>
                <Divider orientation="vertical" sx={{ height: "14px" }} />
                <Typography variant="body2" color="#707070">
                  DOB -{" "}
                  <Box component="span" color="#212121">
                    {carePlanDetail?.patient?.dob
                      ? dateFormat(carePlanDetail?.patient?.dob)
                      : "NA"}
                  </Box>
                </Typography>
                <Divider orientation="vertical" sx={{ height: "14px" }} />
                <Typography variant="body2" color="#707070">
                  MRN -{" "}
                  <Box component="span" color="#212121">
                    {carePlanDetail?.patient?.mrn
                      ? carePlanDetail?.patient?.mrn
                      : "NA"}
                  </Box>
                </Typography>
              </Stack>
            )}
          </Stack>
          <Stack direction="row" alignItems="center" gap="16px">
            {/* <Button
            variant="outlined"
            size="small"
            sx={{ textTransform: "capitalize", fontSize: "1.3rem" }}
          >
            Export care Plan
          </Button>
          <Button
            variant="contained"
            size="small"
            sx={{ textTransform: "capitalize", fontSize: "1.3rem" }}
          >
            Review & Sign Care plan 
          </Button> */}
            {!loadingCarePlan &&
              carePlanDetail?.progress_percentage !== 100 && (
                <Box width="276px">
                  <CarePlanProgress progressValue={progressValue} />
                </Box>
              )}
            {carePlanDetail?.progress_percentage === 99 && (
              <Button variant="contained" onClick={openModalHandler}>
                Save Care Plan
              </Button>
            )}

            {!loadingCarePlan && showEditButton && (
              <Button
                variant="contained"
                startIcon={<EditCarePlanIcon />}
                sx={{ textTransform: "capitalize" }}
                onClick={editCarePlanHandler}
              >
                Edit Care Plan
              </Button>
            )}

            {carePlanDetail?.progress_percentage === 100 &&
              showUpdateCarePlanButton && (
                <Button
                  variant="contained"
                  startIcon={<EditCarePlanIcon />}
                  sx={{ textTransform: "capitalize" }}
                  onClick={openModalHandler}
                >
                  Update Care Plan
                </Button>
              )}

            {carePlanDetail?.progress_percentage !== 100 ||
            carePlanDetail?.progress_percentage === 99 ||
            showEditButton ||
            showUpdateCarePlanButton ? (
              <Divider orientation="vertical" sx={{ height: "14px" }} />
            ) : (
              <></>
            )}

            <IconButton
              sx={{ padding: "4px" }}
              onClick={() => navigate(`/patient/${patientId}?tab=overview`)}
            >
              <HighlightOffRoundedIcon />
            </IconButton>
          </Stack>
        </Stack>
      </StyledAppBar>
    </>
  );
};

export default CarePlanNavbar;
